import { BASKET_CHANGED } from '../topics';
import { useState } from "preact/hooks";

declare global {
    interface Window {
        dataLayer: any[];
    }
}

declare const dataLayer: any[];

if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
}

import h, { FunctionComponent } from 'preact';
import { useReducer } from 'preact/hooks';
import Api from '../common/api';
import { BasketItem, BasketMessage, DeliveryOption, Surcharge } from '../types';
import BasketChangeMessages from './../buying-options/Messages';
import Messages from './Messages';
import PriceList from './PriceList';
import BasketTable from './BasketTable';
import { OrderType } from './index';
import Checkout from './Checkout';
import OrderTypes from './OrderTypes';
import reducer, { State } from '../common/basket-reducer';
declare const __PRODUCT__: any;

const Basket: FunctionComponent<BasketProps> = ({ initialBasketItems, orderType, orderTypes, deliveryOptions, deliveryOption, deliveryPrice, errors, surcharges, subtotal, subtotalExcludingSurcharges, subtotalExcludingDelivery, tax, taxExcludingDelivery, total, csrfToken }) => {
    const initialMessage: string = '';

    const initialState: State = {
        isProcessing: false,
        isOpen: false,
        basketItems: initialBasketItems,
        deliveryTax: null,
        freeDelivery: '',
        errorMessage: initialMessage,
        warningMessage: initialMessage,
        successMessage: initialMessage,
        orderType, errors, surcharges, subtotal, subtotalExcludingSurcharges, subtotalExcludingDelivery, tax, taxExcludingDelivery, total, deliveryOption, deliveryPrice,
    };

    const basketItems = initialBasketItems.map(bi => {
        return {
            ...bi,
            name: bi.product_name,
            variant: bi.colour + ' ' + bi.size,
            unitPrice: bi.unit_price,
        };
    });

    window.dataLayer = window.dataLayer || [];
    dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        'event': 'view_cart',
        'ecommerce': {
            'checkout': {
                'actionField': {'step': 1, 'option': 'View Cart'},
                'products': basketItems
            }
        },
        'ga4_ecommerce': {
            'items': basketItems
        }
    });

    // Message states
    const [ state, dispatch ] = useReducer(reducer, initialState);
    const [ selectedOrderType, setSelectedOrderType ] = useState(orderType);

    const changeItemQuantity = (item: BasketItem, quantity: number): void => {
        dispatch({ type: 'changingItemQuantity' });
        Api.changeQuantityPromise(item.sku, quantity)
            .then(extBasket => {
                dispatch({ type: 'changedItemQuantity', basket: extBasket });
                PubSub.publish(BASKET_CHANGED, extBasket);

                const success = quantity > 0 ? 'Item has been updated' : 'Item has been removed from your basket';
                dispatch({ type: 'updateMessages', error: initialMessage, warning: initialMessage, success: success });
            })
            .catch(e => {
                dispatch({ type: 'updateMessages', error: 'Unable to update item', warning: initialMessage, success: initialMessage });
            })
            .then(e => {
                const alertsWrapper = document.querySelector('#js-alerts-wrapper');
                if (alertsWrapper !== null) {
                    alertsWrapper.scrollIntoView({ behavior: 'smooth' });
                }
            })
        ;
    };

    const removeItem = (item: BasketItem): void => {
        // Remove from cart
        (window as any).dataLayer = (window as any).dataLayer || [];
        window.dataLayer = window.dataLayer || [];
        dataLayer.push({ ecommerce: null });

        const itemToDelete = {
            ...item,
            name: item.product_name,
            price: item.unit_price,
            quantity: item.quantity,
            variant: item.colour + ' ' + item.size,
        };

        window.dataLayer.push({
            'event': 'remove_from_cart',
            'ecommerce': {
                'remove': {
                    'products': [itemToDelete]
                }
            },
            'ga4_ecommerce': {
                'currency': 'GBP',
                'value': item.total,
                'items': [itemToDelete]
            }
        });

        dispatch({ type: 'removingItem', basketItem: item });
        Api.removeItemPromise(item.sku)
            .then(extBasket => {
                dispatch({ type: 'removedItem', basket: extBasket });
                dispatch({ type: 'updateMessages', error: initialMessage, warning: initialMessage, success: 'Item has been removed from your basket' });
                PubSub.publish(BASKET_CHANGED, extBasket);
            })
            .catch(e => {
                dispatch({ type: 'updateMessages', error: 'Unable to remove item from the basket', warning: initialMessage, success: initialMessage });
            })
            .then(e => {
                const alertsWrapper = document.querySelector('#js-alerts-wrapper');
                if (alertsWrapper !== null) {
                    alertsWrapper.scrollIntoView({ behavior: 'smooth' });
                }
            })
        ;
    };

    const changeOrderType = (orderType: string): void => {
        setSelectedOrderType(orderType);

        dispatch({ type: 'changingOrderType' });
        Api.setOrderTypePromise(orderType)
            .then(extBasket => {
                dispatch({ type: 'changedOrderType', basket: extBasket });
                dispatch({ type: 'updateMessages', error: initialMessage, warning: initialMessage, success: 'Order type successfully set' });
            })
            .catch(e => {
                dispatch({ type: 'updateMessages', error: 'Unable to update order type.', warning: initialMessage, success: initialMessage });
            })
            .then(e => {
                const alertsWrapper = document.querySelector('#js-alerts-wrapper');
                if (alertsWrapper !== null) {
                    alertsWrapper.scrollIntoView({ behavior: 'smooth' });
                }
            })
        ;
    };

    return <>
        <BasketChangeMessages
            success={state.successMessage}
            error={state.errorMessage}
            warning={state.warningMessage}
            isSpecialKnitwear={false}
        />
        <Messages errors={state.errors} warnings={state.surcharges}/>
        <div id="duplicate-checkout" class="d-flex flex-column flex-md-row justify-content-end py-5">
            <OrderTypes
                selectedOrderType={selectedOrderType}
                orderTypes={orderTypes}
                canChange={!state.isProcessing}
                onChange={changeOrderType}
                selectId="duplicate-checkout-forwardordering"
            />
            <Checkout canCheckout={!state.isProcessing && state.errors.length === 0} />
        </div>
        <PriceList />
        {(state.basketItems.length > 0) ? <div class="basket pt-3">
            <div id="js-basket-table-wrapper">
                <BasketTable
                    basketItems={state.basketItems}
                    surcharges={state.surcharges}
                    deliveryOptions={deliveryOptions}
                    subtotal={state.subtotal}
                    subtotalExcludingDelivery={state.subtotalExcludingDelivery}
                    tax={state.tax}
                    taxExcludingDelivery={state.taxExcludingDelivery}
                    total={state.total}
                    deliveryOption={state.deliveryOption}
                    deliveryPrice={state.deliveryPrice}
                    orderType={state.orderType}
                    isProcessing={state.isProcessing}
                    onChangeItemQuantity={changeItemQuantity}
                    onRemoveItem={removeItem}
                />

                <div class="d-flex flex-column flex-md-row justify-content-end pt-4">
                    <OrderTypes
                        selectedOrderType={selectedOrderType}
                        orderTypes={orderTypes}
                        canChange={!state.isProcessing}
                        onChange={changeOrderType}
                        selectId={null}
                    />
                    <Checkout canCheckout={!state.isProcessing && state.errors.length === 0} />
                </div>
            </div>
        </div> : <h2 className="h3 text-blue font-weight-bolder mb-5">
            Your basket is empty
        </h2>}
    </>;
}

interface BasketProps {
    initialBasketItems: Array<BasketItem>;
    orderType: string | null;
    orderTypes: Array<OrderType>;
    deliveryOptions: Array<DeliveryOption>;
    errors: Array<BasketMessage>;
    surcharges: Array<Surcharge>;
    subtotal: string;
    subtotalExcludingSurcharges: string;
    subtotalExcludingDelivery: string;
    tax: string;
    taxExcludingDelivery: string;
    total: string;
    deliveryOption: number | null;
    deliveryPrice: string | null;
    csrfToken: string;
}

export default Basket;
